import * as React from 'react';
import classnames from 'classnames';

import Collapsible from 'components/collapsible/deprecated/Collapsible';
import PageSection from 'components/page/page-section/PageSection';
import FormField from 'components/form/form-field/FormField';
import Input from 'components/inputs/input/Input';
import ActionButton from 'components/buttons/action-button/ActionButton';
import StatusLabel from 'components/status-label/StatusLabel';
import MoreButton from 'components/buttons/more-button/MoreButton';
import { withModal } from 'components/modal/ModalHOC';
import { withContextMenu } from 'components/context-menu/ContextMenuHOC';

import ClientApprovalContext from './ClientApprovalContext';

import ContentApprovalGalleryModalAssetWithOverlay from 'modules/campaigns/components/content-approval/ContentApprovalGalleryModalAssetWithOverlay';
import InfluencerEntity from 'modules/influencers/components/influencer-entity/InfluencerEntity';
import DeliverableMediaForApproval from 'modules/deliverables/components/media/parts/media-for-approval/DeliverableMediaForApproval';

import { __t, applyTranslations } from 'utils/language';
import { isContentReviewed, prepareDeliverableAttachmentsForGallery } from 'utils/deliverables';
import { WITH_GALLERY_MODAL_CONFIG } from 'utils/gallery';
import { CONTENT_APPROVAL_STATUS_LABEL_MAP } from 'utils/contentApproval';

import './client-approval-bucket.scss';

import type {
  ClientApprovalBucketItem,
  ClientApprovalBucket as ClientApprovalBucketType,
  DeliverableAttachmentApprovalStatus,
  GalleryModalItem,
  TextEntry
} from 'types';

import type { SyntheticEvent } from 'react';

applyTranslations(require('./client-approval-bucket.i18n.json'));

type Props = {
  bucket: ClientApprovalBucketType,
  onApprovalStatusChanged: (c: number, b: number, a: string) => void,
  onBrandNoteChanged: (b: number, a: string) => void,
  onDownloadLook: (b: number, a: string) => void
};

type State = {
  isFocused: boolean,
  brandRepresentativeNote: string
};

const DivWithGalleryModal = withModal(<div />, WITH_GALLERY_MODAL_CONFIG);

const MoreButtonWithMenu = withContextMenu(MoreButton, {
  position: 'bottom',
  forceEventPropagation: false
});

class ClientApprovalBucket extends React.PureComponent<Props, State> {
  state = {
    isFocused: false,
    brandRepresentativeNote: this.props.bucket.notes.brandRepresentative || ''
  };

  handleNoteFocused = () => {
    this.setState({
      isFocused: true
    });
  };

  handleNoteChanged = (event: SyntheticEvent<TextEntry>): void => {
    const value = event.currentTarget.value;

    this.setState({
      brandRepresentativeNote: value
    });
  };

  handleApprovalStatusChanged = (
    {
      id
    }: {
      id: string | number
    },
    approvalStatus: DeliverableAttachmentApprovalStatus
  ): void => {
    const { bucket, onApprovalStatusChanged } = this.props;

    onApprovalStatusChanged(bucket.id, Number(id), approvalStatus);
  };

  handleNoteSubmitted = (event: SyntheticEvent<TextEntry>): void => {
    const { bucket, onBrandNoteChanged } = this.props;
    const value = event.currentTarget.value;

    onBrandNoteChanged(bucket.id, value.trim());

    this.setState({
      isFocused: false
    });
  };

  handleDownloadLook = (bucketId: number, lookName: string): void => {
    const { onDownloadLook } = this.props;

    if (bucketId) {
      onDownloadLook(bucketId, lookName);
    }
  };

  renderContextMenu(): React.ReactNode {
    const { bucket } = this.props;
    const messageKey = 'client_approval_bucket.context_menu';

    const contextMenuItems = [
      {
        name: 'download-look',
        text: __t(`${messageKey}.download`),
        onClick: () => this.handleDownloadLook(bucket.id, bucket.title)
      }
    ];

    return (
      <MoreButtonWithMenu
        width={20}
        iconHeight={12}
        customColor="black"
        contextMenuProps={{ items: contextMenuItems }}
      />
    );
  }

  // see `ClientApprovalContext.Provider` for notes on why this is necessary
  renderModalThumbnailOverlay = (selectedItem: GalleryModalItem): React.ReactNode | undefined | null => (
    <ClientApprovalContext.Consumer>
      {
        (data) => {
          if (!data) {
            return null;
          }

          const currentItem = data.items.find(item => selectedItem.id === item.id);

          const statusClasses = classnames('client-approval-bucket__thumbnail-overlay-status', {
            [`client-approval-bucket__thumbnail-overlay-status--${currentItem?.approvalStatus}`]: true
          });

          return data.type === 'looks' && currentItem && (
            <div className="client-approval-bucket__thumbnail-overlay">
              <div
                className={statusClasses}
                title={__t(`client_approval_bucket.${currentItem.approvalStatus}`)}
              />
            </div>
          );
        }
      }
    </ClientApprovalContext.Consumer>
  );

  renderOverlayStatusLabel(status: DeliverableAttachmentApprovalStatus): React.ReactNode {
    return (
      <StatusLabel
        type={CONTENT_APPROVAL_STATUS_LABEL_MAP[status]}
        size="large"
        background="transparent"
        label={__t(`client_approval_bucket.${status}`)}
      />
    );
  }

  renderOverlayButtons(item: ClientApprovalBucketItem): React.ReactNode {
    return (
      <React.Fragment>
        <div className="client-approval-bucket__asset-overlay-button">
          <ActionButton
            width="maximum"
            color="tertiary"
            onClick={() => this.handleApprovalStatusChanged(item, 'declined')}
          >
            {__t('client_approval_bucket.button_decline')}
          </ActionButton>
        </div>
        <div className="client-approval-bucket__asset-overlay-button">
          <ActionButton
            width="maximum"
            color="primary"
            onClick={() => this.handleApprovalStatusChanged(item, 'approved')}
          >
            {__t('client_approval_bucket.button_approve')}
          </ActionButton>
        </div>
      </React.Fragment>
    );
  }

  renderModalAssetOverlayBody(selectedItem: GalleryModalItem, currentPosition: number): React.ReactNode | undefined | null {
    // see `ClientApprovalContext.Provider` for notes on why this is necessary
    return (
      <ClientApprovalContext.Consumer>
        {
          (data) => {
            if (!data) {
              return null;
            }

            const currentItem = data.items.find(item => selectedItem.id === item.id);

            return data.type === 'looks' && currentItem && (
              <div
                className="client-approval-bucket__asset-overlay"
                data-index={currentPosition}
              >
                <div className="client-approval-bucket__asset-overlay-status-container">
                  {
                    currentItem.approvalStatus === 'pending' ?
                      this.renderOverlayButtons(currentItem) :
                      this.renderOverlayStatusLabel(currentItem.approvalStatus)
                  }
                </div>
              </div>
            );
          }
        }
      </ClientApprovalContext.Consumer>
    );
  }

  renderCustomModalAsset = (selectedItem: GalleryModalItem, currentPosition: number): JSX.Element => {
    const { bucket } = this.props;

    return (
      <ContentApprovalGalleryModalAssetWithOverlay
        title={{
          value: bucket.title,
          show: true
        }}
        body={this.renderModalAssetOverlayBody(selectedItem, currentPosition)}
        selectedItem={selectedItem}
        currentPosition={currentPosition}
      />
    );
  };

  renderHeader(): React.ReactNode {
    const { bucket } = this.props;

    return (
      <div className="client-approval-bucket__header">
        <div className="client-approval-bucket__entity">
          {
            bucket.influencerProfile && (
              <InfluencerEntity
                preferInstagramDetails
                influencerProfile={bucket.influencerProfile}
              />
            )
          }
        </div>
        <div className="client-approval-bucket__context-menu">
          {this.renderContextMenu()}
        </div>
      </div>
    );
  }

  renderNotes(): React.ReactNode {
    const { bucket } = this.props;
    const { brandRepresentativeNote, isFocused } = this.state;

    return (
      <div className="client-approval-bucket__notes">
        <div className="client-approval-bucket__note">
          <FormField
            label={`${__t('client_approval_bucket.note_account_manager')}:`}
            labelType="highlighted"
            direction="column"
            fontSize="small"
            showRequiredMark={false}
            showValidationIcon={false}
          >
            <div className="client-approval-bucket__note-label">
              {bucket.notes.accountManager || '—'}
            </div>
          </FormField>
        </div>
        <div className="client-approval-bucket__note">
          <FormField
            label={`${__t('client_approval_bucket.note_brand_representative')}:`}
            labelType="highlighted"
            direction="column"
            fontSize="small"
            showRequiredMark={false}
            showValidationIcon={false}
          >
            <Input
              autoFocus={isFocused}
              value={brandRepresentativeNote}
              width="responsive"
              multiline
              onFocus={this.handleNoteFocused}
              onChange={this.handleNoteChanged}
              onBlur={this.handleNoteSubmitted}
            />
          </FormField>
        </div>
      </div>
    );
  }

  renderItems(): React.ReactNode {
    const { bucket } = this.props;
    const galleryItems = prepareDeliverableAttachmentsForGallery(bucket.items);

    // `bucket.items` as context is necessary to dynamically update data inside gallery modal
    return (
      <ul className="client-approval-bucket__items">
        <ClientApprovalContext.Provider
          value={{ // eslint-disable-line react/jsx-no-constructed-context-values
            type: 'looks',
            items: bucket.items
          }}
        >
          {
            bucket.items.map((item, index) => {
              const attachment = {
                ...item,
                assetType: item.mediaType
              };

              const isReviewed = isContentReviewed(attachment);

              return (
                <li
                  key={`client-approval-item-${item.id}`}
                  className="client-approval-bucket__item"
                >
                  <DivWithGalleryModal
                    key={`deliverable-media-attachment-${attachment.id}`}
                    modalContentProps={{
                      items: galleryItems,
                      currentPosition: index,
                      renderCustomAsset: this.renderCustomModalAsset,
                      renderCustomThumbnailOverlay: this.renderModalThumbnailOverlay
                    }}
                  >
                    <DeliverableMediaForApproval
                      showButtons={!isReviewed}
                      showStatus={isReviewed}
                      attachment={attachment}
                      onApprovalStatusChanged={this.handleApprovalStatusChanged}
                      showReset
                    />
                  </DivWithGalleryModal>
                </li>
              );
            })
          }
        </ClientApprovalContext.Provider>
      </ul>
    );
  }

  render() {
    const { bucket } = this.props;
    const { brandRepresentativeNote } = this.state;

    return (
      <PageSection
        title={bucket.title}
        titleProps={{
          color: 'normal',
          size: 'h2',
          customClass: 'client-approval-bucket__title'
        }}
        padding="none"
      >
        <section className="client-approval-bucket">
          {
            bucket.influencerProfile &&
              this.renderHeader()
          }
          <div className="client-approval-bucket__notes-wrapper">
            <Collapsible
              title={__t('client_approval_bucket.notes_label')}
              isTransparent
              arrowPosition="left"
              collapsed={!bucket.notes.accountManager && !brandRepresentativeNote}
            >
              {this.renderNotes()}
            </Collapsible>
          </div>
          {this.renderItems()}
        </section>
      </PageSection>
    );
  }
}

export default ClientApprovalBucket;
